import { Component, OnInit, ViewChild } from '@angular/core';
import { DeviceService } from '../shared/services/device.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, map, startWith } from 'rxjs';
import {
  DeviceTypeKeys,
  DeviceTypes,
} from '../shared/models/device-types-attributes';
import { Sort } from '@angular/material/sort';
import {
  DefaultActivatedFilterItemKeysMobile,
  DeviceCardItem,
  DeviceFilterItem,
  DeviceFilterItems,
  DeviceList,
  DeviceStatus,
  DeviceStatusKeys,
  convertToDeviceCardItem,
} from '../shared/models/device-list.model';
import { trigger, animate, transition, style } from '@angular/animations';
import { DeviceListService } from '../shared/services/device-list.service';
import { LogisticStatusValues } from '../shared/models/device.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-device-list',
  templateUrl: './device-list.component.html',
  styleUrls: ['./device-list.component.scss'],
  animations: [
    trigger('appear', [
      transition(':enter', [
        animate('0ms', style({ height: 0, transform: 'translateX(-100%)' })),
        animate('200ms ease-out', style({ height: '*' })),
        animate('400ms ease-out', style({ transform: 'translateX(0)' })),
      ]),
      transition(':leave', [
        animate('0ms', style({ height: '*', transform: 'translateX(0)' })),
        animate('400ms ease-out', style({ transform: 'translateX(-100%)' })),
        animate('200ms ease-out', style({ height: 0 })),
      ]),
    ]),
  ],
})
export class DeviceListComponent implements OnInit {
  protected mobile: boolean = false;
  protected showTypes: boolean = true;
  private _showSort: boolean = true;
  protected get showSort(): boolean {
    return this._showSort;
  }
  protected set showSort(value: boolean) {
    this._showSort = value;
    this.expandSort = !this.mobile;
  }
  protected expandSort: boolean = true;
  protected isLoading = true;
  protected multiType = false;
  protected showStatusChips = false;

  protected devices: MatTableDataSource<DeviceCardItem> =
    new MatTableDataSource<DeviceCardItem>([]);

  protected deviceObserver: Observable<DeviceCardItem[]>;

  protected deviceTypes: DeviceTypeKeys[] = [
    DeviceTypeKeys.SZT,
    DeviceTypeKeys.GATE,
    DeviceTypeKeys.SIMCARD,
  ];

  protected filterItems: DeviceFilterItem[] = DeviceFilterItems;

  protected showFilterItem(item: DeviceFilterItem): boolean {
    return !item.distinctFilter || this.isDistinctByAttr(item.control);
  }

  protected get visibleFilterItems(): DeviceFilterItem[] {
    return this.filterItems.filter(this.showFilterItem.bind(this));
  }

  protected get allFilterChecked(): boolean {
    const formFilterItems = this.filters.get('filterItems')?.value ?? [];
    return this.visibleFilterItems.every((item) =>
      Boolean(formFilterItems[item.index])
    );
  }

  protected get anyFilterChecked(): boolean {
    const formFilterItems = this.filters.get('filterItems')?.value ?? [];
    const checked = this.visibleFilterItems.filter((item) =>
      Boolean(formFilterItems[item.index])
    ).length;
    return checked > 0 && checked < this.visibleFilterItems.length;
  }

  protected chipsFullData: Record<string, any[]> = {
    externalDeviceTypeCode: this.deviceTypes,
    deviceStatusListbox: Array.from(DeviceStatusKeys),
  };

  @ViewChild('deviceListPaginator') private paginator!: MatPaginator;

  protected readonly filters = new FormGroup({
    deviceId: new FormControl('', { nonNullable: true }),
    externalDeviceId: new FormControl('', { nonNullable: true }),
    externalDeviceTypeCode: new FormControl(
      this.chipsFullData['externalDeviceTypeCode']
    ),
    deviceStatusListbox: new FormControl(
      this.chipsFullData['deviceStatusListbox']
    ),
    logisticStatus: new FormControl('', { nonNullable: true }),
    logisticStatusDesc: new FormControl('', { nonNullable: true }),
    gateFactorySn: new FormControl('', { nonNullable: true }),
    customerName: new FormControl('', { nonNullable: true }),
    filterItems: new FormArray(
      this.filterItems.map((item) => new FormControl(item.activated))
    ),
  });

  protected clearFilter(name: string, $event: MouseEvent) {
    $event.stopPropagation();
    this.filters.get(name)?.setValue('');
  }

  protected emptyFilter(name: string): boolean {
    const item = this.filters.get(name);
    return item?.value === '' || Boolean(item?.disabled);
  }

  protected isFilterActive(index: number): boolean {
    return Boolean(this.filters.get('filterItems')?.value[index]);
  }

  protected get activeCount(): number {
    return this.filters.get('filterItems')?.value.filter(Boolean).length ?? 0;
  }

  protected get filterItemFlexStyle(): string {
    return `1 0 calc(${100 / this.activeCount}% - 10px)`;
  }

  protected clearChips(name: string = 'externalDeviceTypeCode') {
    this.filters
      .get(name)
      ?.setValue(this.isFullChips(name) ? [] : this.chipsFullData[name]);
  }

  protected isFullChips(name: string = 'externalDeviceTypeCode'): boolean {
    return (
      this.filters.get(name)?.value?.length === this.chipsFullData[name].length
    );
  }

  protected getCountByStatus(status: DeviceStatus): number {
    return this.devices.data.filter(
      (device) => device.operationStatus === status
    ).length;
  }

  protected getCountByType(type: DeviceTypeKeys): number {
    return this.devices.data.filter(
      (device) => device.externalDeviceTypeCode === type
    ).length;
  }

  constructor(
    private deviceService: DeviceService,
    private deviceListService: DeviceListService,
    private breakpointObserver: BreakpointObserver,
    private translate: TranslateService
  ) {
    this.devices.filterPredicate = this.tableFilter();
    this.devices.data = [];
    this.deviceObserver = this.devices.connect();
    if (this.breakpointObserver.isMatched(Breakpoints.HandsetPortrait)) {
      this.filterItems.forEach((item, i) => {
        item.activated = DefaultActivatedFilterItemKeysMobile.includes(
          item.control
        );
        (this.filters.get('filterItems') as FormArray)
          .at(i)
          .setValue(item.activated);
      });
    }
    const filterPreferences = this.deviceListService.filterPreferences;
    this.filterItems.forEach((item, i) => {
      item.activated = filterPreferences[item.control] ?? item.activated;
      (this.filters.get('filterItems') as FormArray)
        .at(i)
        .setValue(item.activated);
    });
    this.logValuesObservable = this.filters
      .get('logisticStatus')
      ?.valueChanges.pipe(
        startWith(''),
        map((value) => (value ? this.filterLogValues(value) : this.LogValues))
      )!;
  }

  ngOnInit(): void {
    this.loadDevices();
    this.filters.valueChanges.subscribe((filter) => {
      this.devices.filter = JSON.stringify(filter);
    });
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).subscribe({
      next: (res) => {
        this.mobile = res.matches;
        this.showTypes = !res.matches;
        if (res.matches) {
          this.showSort = false;
        }
      },
    });
  }

  protected loadDevices() {
    this.isLoading = true;
    this.deviceService
      .getDevicesV2()
      .subscribe({
        next: (data: DeviceList) => {
          const newData = data.gates
            .concat(data.sims)
            .concat(data.szts)
            .map(convertToDeviceCardItem);
          this.devices.data = newData;
          this.devices.paginator = this.paginator;
          if (!this.multiCustomer) {
            this.filters
              .get('customerName')
              ?.setValue(this.devices.data[0]?.customerName);
            this.filters.get('customerName')?.disable();
          }
          this.multiType = data.sims?.length > 0 || data.szts?.length > 0;
        },
      })
      .add(() => {
        this.isLoading = false;
      });
  }

  private tableFilter(): (data: any, filter: string) => boolean {
    return (data: DeviceCardItem, filter: string): boolean => {
      const searchTerms = JSON.parse(filter);
      return Object.keys(searchTerms)
        .filter(
          (key) =>
            (searchTerms[key]?.length || Array.isArray(searchTerms[key])) &&
            key !== 'filterItems'
        )
        .every((key) => {
          if (key === 'externalDeviceTypeCode') {
            return (searchTerms[key] as DeviceTypeKeys[]).includes(
              data.externalDeviceTypeCode
            );
          }
          if (key === 'deviceStatusListbox') {
            const terms = searchTerms[key] as DeviceStatus[];
            return (
              data.externalDeviceTypeCode !== DeviceTypeKeys.SZT ||
              terms.length === DeviceStatusKeys.length ||
              terms.includes(data.operationStatus as DeviceStatus)
            );
          }
          return data[key as keyof DeviceCardItem]
            ?.toString()
            .toLowerCase()
            .includes(searchTerms[key].toString().toLowerCase());
        });
    };
  }

  protected sortData(sort: Sort) {
    const asc = sort.direction === 'asc' ? 1 : -1;
    this.devices.data = this.devices.data.sort((a, b) => {
      if (sort.active === 'externalDeviceTypeCode') {
        return (
          this.translate
            .instant(DeviceTypes[a.externalDeviceTypeCode].translateString)
            .localeCompare(
              this.translate.instant(
                DeviceTypes[b.externalDeviceTypeCode].translateString
              )
            ) * asc
        );
      }
      const valA = a[sort.active as keyof DeviceCardItem] ?? '';
      const valB = b[sort.active as keyof DeviceCardItem] ?? '';
      if (typeof valA === 'number' && typeof valB === 'number') {
        return (valA - valB) * asc;
      }
      return String(valA).localeCompare(String(valB)) * asc;
    });
  }

  protected readonly DeviceTypes = DeviceTypes;
  protected readonly DeviceTypeKeys = DeviceTypeKeys;
  protected readonly StatusKeys = DeviceStatusKeys;

  protected get multiCustomer() {
    return this.isDistinctByAttr('customerId');
  }

  protected get multiLogistic() {
    return this.isDistinctByAttr('logisticStatus');
  }

  protected isDistinctByAttr(attr: keyof DeviceCardItem): boolean {
    return Boolean(
      this.devices.data.length &&
        this.devices.data.some(
          (device) => device[attr] !== this.devices.data[0][attr]
        )
    );
  }

  protected get sztFilterOn(): boolean {
    return Boolean(
      this.filters
        .get('externalDeviceTypeCode')
        ?.value?.includes(DeviceTypeKeys.SZT)
    );
  }

  protected menuFilterSelect() {
    const filterValues =
      (this.filters.get('filterItems')?.value as boolean[]) ?? [];
    this.deviceListService.filterPreferences = this.filterItems.reduce(
      (obj, item) => ({
        ...obj,
        [item.control]: filterValues[item.index],
      }),
      {}
    );
  }

  protected menuFilterAllSelect(on: boolean) {
    (this.filters.get('filterItems') as FormArray).controls.forEach((item) => {
      item.setValue(on);
    });
    this.menuFilterSelect();
  }

  protected readonly LogValues = LogisticStatusValues;
  private filterLogValues(value: string): string[] {
    const lowerValue = value.toLowerCase();
    return this.LogValues.filter(
      (item) => value && item.toLowerCase().includes(lowerValue)
    );
  }
  protected logValuesObservable: Observable<string[]>;

  protected statusSelected(status: DeviceStatus): boolean {
    return Boolean(
      this.filters
        .get('deviceStatusListbox')
        ?.value?.includes(status as DeviceStatus)
    );
  }

  protected typeSelected(type: DeviceTypeKeys): boolean {
    return Boolean(
      this.filters
        .get('externalDeviceTypeCode')
        ?.value?.includes(type as DeviceTypeKeys)
    );
  }

  protected countByLogisticStatus(status: string): number {
    return this.devices.data.filter(
      (device) => device.logisticStatus === status
    ).length;
  }
}
